import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Users,
  GraduationCap,
  FileText,
  Calendar,
  X,
  Clock,
  TrendingUp,
  Award,
  BarChart3,
  Image as ImageIcon,
  Sparkles,
  Loader2,
  Upload
} from 'lucide-react';

export default function CourseManagement() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [courseStats, setCourseStats] = useState({});

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    selfPacedEnabled: false,
    estimatedDuration: '',
    prerequisites: '',
    imageUrl: ''
  });

  const [editForm, setEditForm] = useState({
    id: '',
    name: '',
    description: '',
    selfPacedEnabled: false,
    estimatedDuration: '',
    prerequisites: '',
    imageUrl: ''
  });
  const [generatingAI, setGeneratingAI] = useState(false);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch courses');
      }

      const data = await response.json();
      setCourses(data);

      // Fetch stats for each course (units count, students enrolled)
      const statsPromises = data.map(async (course) => {
        try {
          // Fetch units for this course
          const unitsResponse = await fetch(`${API_BASE_URL}/units?courseId=${course.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const units = unitsResponse.ok ? await unitsResponse.json() : [];

          // Count students enrolled in units of this course
          let totalStudents = 0;
          for (const unit of units) {
            try {
              const studentsResponse = await fetch(`${API_BASE_URL}/students?unitId=${unit.id}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              const students = studentsResponse.ok ? await studentsResponse.json() : [];
              totalStudents += students.length;
            } catch (err) {
              // Continue with next unit if this fails
            }
          }

          return {
            courseId: course.id,
            units: units.length || 0,
            students: totalStudents,
            activeUnits: units.filter(u => u.status === 'active').length || 0
          };
        } catch (err) {
          return {
            courseId: course.id,
            units: 0,
            students: 0,
            activeUnits: 0
          };
        }
      });

      const stats = await Promise.all(statsPromises);
      const statsMap = {};
      stats.forEach(stat => {
        statsMap[stat.courseId] = {
          units: stat.units,
          students: stat.students,
          activeUnits: stat.activeUnits
        };
      });
      setCourseStats(statsMap);

    } catch (error) {
      setError(error.message);
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleCreateCourse = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(createForm),
      });

      if (!response.ok) {
        throw new Error('Failed to create course');
      }

      const newCourse = await response.json();
      setCourses(prev => [...prev, newCourse]);
      setCreateForm({
        name: '',
        description: '',
        selfPacedEnabled: false,
        estimatedDuration: '',
        prerequisites: '',
        imageUrl: ''
      });
      setShowCreateForm(false);

    } catch (error) {
      setError('Failed to create course: ' + error.message);
      console.error('Error creating course:', error);
    }
  };

  const handleGenerateAIBanner = async (name, description) => {
    if (!name) {
      alert('Please enter a course name first.');
      return;
    }

    try {
      setGeneratingAI(true);
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/ai/generate-banner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name, description }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate smart banner');
      }

      const data = await response.json();
      setCreateForm(prev => ({ ...prev, imageUrl: data.imageUrl }));
    } catch (error) {
      console.error('Error generating automated banner:', error);
      alert('Failed to generate smart banner. Please try again.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('banner', file);

    try {
      setGeneratingAI(true); // Reuse generatingAI state for loading
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/api/images/banners`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload banner');
      }

      const data = await response.json();
      setCreateForm(prev => ({ ...prev, imageUrl: data.imageUrl }));
    } catch (error) {
      console.error('Error uploading banner:', error);
      alert('Failed to upload banner. Please try again.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleEditClick = (course) => {
    setEditForm({
      id: course.id,
      name: course.name,
      description: course.description || '',
      selfPacedEnabled: course.selfPacedEnabled || false,
      estimatedDuration: course.estimatedDuration || '',
      prerequisites: course.prerequisites || '',
      imageUrl: course.imageUrl || ''
    });
    setShowEditModal(true);
    setShowDetailsModal(false);
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/courses/${editForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        throw new Error('Failed to update course');
      }

      const updatedCourse = await response.json();
      setCourses(prev => prev.map(c => c.id === updatedCourse.id ? updatedCourse : c));
      setShowEditModal(false);
      
      // Refresh detailed view if it was open
      if (selectedCourse && selectedCourse.id === updatedCourse.id) {
        handleViewDetails(updatedCourse);
      }

    } catch (error) {
      setError('Failed to update course: ' + error.message);
      console.error('Error updating course:', error);
    }
  };

  const handleViewDetails = async (course) => {
    try {
      // Fetch detailed course data
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/courses/${course.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch course details');
      }

      const detailedCourse = await response.json();

      // Fetch associated units
      const unitsResponse = await fetch(`${API_BASE_URL}/units?courseId=${course.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const units = unitsResponse.ok ? await unitsResponse.json() : [];

      setSelectedCourse({
        ...detailedCourse,
        units: units,
        stats: courseStats[course.id] || { units: 0, students: 0, activeUnits: 0 }
      });
      setShowDetailsModal(true);

    } catch (error) {
      setError('Failed to fetch course details: ' + error.message);
      console.error('Error fetching course details:', error);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? This will also affect associated units.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/courses/${courseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete course');
      }

      setCourses(prev => prev.filter(course => course.id !== courseId));

    } catch (error) {
      setError('Failed to delete course: ' + error.message);
      console.error('Error deleting course:', error);
    }
  };

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <BookOpen className="mr-3 h-8 w-8" />
              Course Management
            </h1>
            <p className="mt-2 text-purple-100">
              Manage academic courses and track enrollment
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{courses.length}</div>
            <div className="text-purple-100">Total Courses</div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Controls Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-2">
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Course
            </button>
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCourses.map((course) => {
          const stats = courseStats[course.id] || { units: 0, students: 0, activeUnits: 0 };

          return (
            <div key={course.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg flex items-center justify-center text-white">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div className="ml-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">{course.name}</h3>
                      {course.selfPacedEnabled && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                          Self-Paced
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 block">{course.description || 'No description provided'}</p>
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    onClick={() => handleViewDetails(course)}
                    className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEditClick(course)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit Course"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteCourse(course.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Course"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <FileText className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                  <div className="text-xl font-bold text-blue-900">{stats.units}</div>
                  <div className="text-xs text-blue-700">Units</div>
                </div>

                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <GraduationCap className="h-4 w-4 text-green-600 mx-auto mb-1" />
                  <div className="text-xl font-bold text-green-900">{stats.students}</div>
                  <div className="text-xs text-green-700">Students</div>
                </div>

                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <Clock className="h-4 w-4 text-orange-600 mx-auto mb-1" />
                  <div className="text-xl font-bold text-orange-900">{stats.activeUnits}</div>
                  <div className="text-xs text-orange-700">Active</div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  Created: {new Date(course.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Course Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Create New Course</h2>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateCourse} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Name
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                  placeholder="Course description..."
                />
              </div>

              <div className="flex items-center space-x-2 py-2">
                <input
                  type="checkbox"
                  id="selfPacedEnabled"
                  checked={createForm.selfPacedEnabled}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, selfPacedEnabled: e.target.checked }))}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="selfPacedEnabled" className="text-sm font-medium text-gray-700">
                  Enable Self-Paced Learning
                </label>
              </div>

              {createForm.selfPacedEnabled && (
                <div className="space-y-4 pl-6 border-l-2 border-purple-100">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Duration
                    </label>
                    <input
                      type="text"
                      value={createForm.estimatedDuration}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., 8 weeks"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prerequisites
                    </label>
                    <textarea
                      value={createForm.prerequisites}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, prerequisites: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="2"
                      placeholder="List any prerequisites..."
                    />
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Banner Image
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={createForm.imageUrl}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Image URL or use smart generation..."
                    />
                    <div className="flex gap-1">
                      <label className="flex items-center px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 cursor-pointer transition-all border border-gray-200">
                        <Upload className="h-4 w-4" />
                        <input type="file" className="hidden" onChange={handleFileUpload} accept="image/*" />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleGenerateAIBanner(createForm.name, createForm.description)}
                        disabled={generatingAI || !createForm.name}
                        className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all text-sm font-medium"
                      >
                        {generatingAI ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Sparkles className="h-4 w-4 mr-2" />
                        )}
                        Smart Gen
                      </button>
                    </div>
                  </div>
                  
                  {createForm.imageUrl && (
                    <div className="relative rounded-lg overflow-hidden border border-gray-200 aspect-video bg-gray-50">
                      <img 
                        src={createForm.imageUrl} 
                        alt="Course Banner Preview" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setCreateForm(prev => ({ ...prev, imageUrl: '' }))}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                      >
                        <X className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Create Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Course Details Modal */}
      {showDetailsModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold flex items-center">
                <BookOpen className="h-6 w-6 mr-2 text-purple-600" />
                {selectedCourse.name}
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Course Information */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Course Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Description:</span>
                        <p className="text-gray-900 mt-1">{selectedCourse.description || 'No description provided'}</p>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-700">
                          Created: {new Date(selectedCourse.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Statistics</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <FileText className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-blue-900">{selectedCourse.stats.units}</div>
                      <div className="text-sm text-blue-700">Total Units</div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <GraduationCap className="h-6 w-6 text-green-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-green-900">{selectedCourse.stats.students}</div>
                      <div className="text-sm text-green-700">Enrolled Students</div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                      <Clock className="h-6 w-6 text-orange-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-orange-900">{selectedCourse.stats.activeUnits}</div>
                      <div className="text-sm text-orange-700">Active Units</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Units List */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Course Units</h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  {selectedCourse.units.length > 0 ? (
                    <div className="space-y-3">
                      {selectedCourse.units.map((unit) => (
                        <div key={unit.id} className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{unit.name}</h4>
                              <p className="text-sm text-gray-500">{unit.description || 'No description'}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs ${unit.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : unit.status === 'completed'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                              }`}>
                              {unit.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No units assigned to this course yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => handleEditClick(selectedCourse)}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Edit Course
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Edit Course</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateCourse} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                  placeholder="Course description..."
                />
              </div>

              <div className="flex items-center space-x-2 py-2">
                <input
                  type="checkbox"
                  id="editSelfPacedEnabled"
                  checked={editForm.selfPacedEnabled}
                  onChange={(e) => setEditForm(prev => ({ ...prev, selfPacedEnabled: e.target.checked }))}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="editSelfPacedEnabled" className="text-sm font-medium text-gray-700">
                  Enable Self-Paced Learning
                </label>
              </div>

              {editForm.selfPacedEnabled && (
                <div className="space-y-4 pl-6 border-l-2 border-purple-100">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Duration
                    </label>
                    <input
                      type="text"
                      value={editForm.estimatedDuration}
                      onChange={(e) => setEditForm(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., 8 weeks"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prerequisites
                    </label>
                    <textarea
                      value={editForm.prerequisites}
                      onChange={(e) => setEditForm(prev => ({ ...prev, prerequisites: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="2"
                      placeholder="List any prerequisites..."
                    />
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Banner Image
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editForm.imageUrl}
                      onChange={(e) => setEditForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Image URL or use smart generation..."
                    />
                    <div className="flex gap-1">
                      <label className="flex items-center px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 cursor-pointer transition-all border border-gray-200">
                        <Upload className="h-4 w-4" />
                        <input type="file" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('banner', file);
                          try {
                            setGeneratingAI(true);
                            const token = localStorage.getItem('token');
                            const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api').replace('/api', '');
                            const response = await fetch(`${API_BASE_URL}/api/images/banners`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` },
                              body: formData,
                            });
                            if (!response.ok) {
                              throw new Error('Upload failed');
                            }
                            const data = await response.json();
                            setEditForm(prev => ({ ...prev, imageUrl: data.imageUrl }));
                          } catch (err) {
                            alert(err.message);
                          } finally {
                            setGeneratingAI(false);
                          }
                        }} accept="image/*" />
                      </label>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setGeneratingAI(true);
                            const token = localStorage.getItem('token');
                            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
                            const response = await fetch(`${API_BASE_URL}/ai/generate-banner`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                              body: JSON.stringify({ name: editForm.name, description: editForm.description }),
                            });
                            if (!response.ok) {
                              const errorData = await response.json().catch(() => ({}));
                              throw new Error(errorData.message || 'Automated Generation failed');
                            }
                            const data = await response.json();
                            setEditForm(prev => ({ ...prev, imageUrl: data.imageUrl }));
                          } catch (err) {
                            console.error('Automated Gen Error:', err);
                            alert(err.message);
                          } finally {
                            setGeneratingAI(false);
                          }
                        }}
                        disabled={generatingAI || !editForm.name}
                        className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all text-sm font-medium"
                      >
                        {generatingAI ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                        Smart Gen
                      </button>
                    </div>
                  </div>
                  {editForm.imageUrl && (
                    <div className="relative rounded-lg overflow-hidden border border-gray-200 aspect-video bg-gray-50">
                      {generatingAI ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                        </div>
                      ) : null}
                      <img 
                        key={editForm.imageUrl}
                        src={editForm.imageUrl} 
                        alt="Preview" 
                        className="w-full h-full object-cover" 
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/1200x400?text=Image+Load+Error';
                        }}
                      />
                      <button 
                        type="button" 
                        onClick={() => setEditForm(prev => ({ ...prev, imageUrl: '' }))} 
                        className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 z-20"
                      >
                        <X className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredCourses.length === 0 && !loading && (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating your first course.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Course
            </button>
          )}
        </div>
      )}
    </div>
  );
}
